import type { FC, ReactNode } from 'react'

import Image from 'next/image'

import { stats, themes } from '@/constants/metric'
import { SectionHeading } from '@/components/generals/section-heading'
import { Tag } from '@/components/generals/tag'

export const AboutSection: FC = (): ReactNode => {
    return (
        <section id='about' aria-labelledby='about-title' className='scroll-mt-[96px] px-0 py-20 tab:px-[60px] tab:py-28'>
            <div className='fw-container flex flex-col items-center gap-12 tab:gap-16'>
                <SectionHeading
                    tag='Overview'
                    title={<span id='about-title'>Measure what matters.</span>}
                    description='Metric brings together the people who decide with numbers: analysts, product leads, engineers, and founders arguing about what is worth counting.'
                />

                <div
                    data-reveal='up'
                    className='grid w-full grid-cols-1 overflow-hidden rounded-[9px] border border-line bg-surface sm:grid-cols-3'
                >
                    {stats.map((stat, index) => (
                        <div
                            key={stat.label}
                            className={[
                                'flex flex-col items-center justify-center gap-2 px-6 py-10 text-center',
                                index > 0 ? 'border-t border-line sm:border-l sm:border-t-0' : ''
                            ].join(' ')}
                        >
                            <span className='font-heading text-[43px] font-medium leading-[1em] tracking-[-0.04em] text-ink tab:text-[54px]'>
                                {stat.value}
                            </span>

                            <span className='font-body text-[15px] leading-[1.4em] text-ink-mute'>
                                {stat.label}
                            </span>
                        </div>
                    ))}
                </div>

                <div data-reveal='up' className='pt-4'>
                    <Tag>
                        Main themes
                    </Tag>
                </div>

                <ul className='grid w-full grid-cols-1 gap-8 sm:grid-cols-2 desk:grid-cols-3 desk:gap-6'>
                    {themes.map((theme, index) => (
                        <li
                            key={theme.index}
                            data-reveal='up'
                            data-reveal-delay={0.08 * index}
                            className='group flex flex-col gap-5'
                        >
                            <div className='relative aspect-[4/3] w-full overflow-hidden rounded-[9px] border border-line'>
                                <Image
                                    src={theme.image}
                                    alt={theme.title}
                                    fill
                                    sizes='(max-width: 810px) 100vw, (max-width: 1360px) 50vw, 33vw'
                                    className='object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]'
                                />
                            </div>

                            <div className='flex flex-col gap-3'>
                                <span className='font-sans text-[14px] font-medium uppercase leading-[1em] tracking-[0.02em] text-ink-mute'>
                                    {theme.index}
                                </span>

                                <h3 className='font-sans text-[22px] font-medium leading-[1.2em] text-ink tab:text-[26px]'>
                                    {theme.title}
                                </h3>

                                <p className='font-body text-[16px] leading-[1.4em] text-ink-soft'>
                                    {theme.copy}
                                </p>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}
